import React, { useCallback, useEffect, useState } from "react";
import { Form, Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  TbPuzzle, TbPlus, TbEdit, TbTrash, TbCheck, TbX,
  TbArrowsSort,
} from "react-icons/tb";
import aiChatbotsService, { ModuleCatalogItem } from "../../features/ai_chatbots/aiChatbotsService";
import { showSuccess, showError, showConfirm } from "../../utils/bmsToast";

const EMPTY_FORM = { module_key: "", name: "", description: "", is_enabled_globally: true, ui_order: 0 };

const ModuleCatalogPage: React.FC = () => {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState<ModuleCatalogItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await aiChatbotsService.getModulesCatalog();
      setCatalog(res || []);
    } catch (e: any) {
      showError(e?.response?.data?.detail || "Failed to load modules");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingKey(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (item: ModuleCatalogItem) => {
    setEditingKey(item.module_key);
    setForm({
      module_key: item.module_key,
      name: item.name,
      description: item.description || "",
      is_enabled_globally: item.is_enabled_globally,
      ui_order: item.ui_order,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.module_key.trim() || !form.name.trim()) return;
    setSaving(true);
    try {
      if (editingKey) {
        await aiChatbotsService.updateModuleCatalogItem(editingKey, {
          name: form.name,
          description: form.description || undefined,
          is_enabled_globally: form.is_enabled_globally,
          ui_order: form.ui_order,
        });
        showSuccess(t("mod_catalog_updated"));
      } else {
        await aiChatbotsService.createModuleCatalogItem({
          module_key: form.module_key.trim().toLowerCase(),
          name: form.name.trim(),
          description: form.description || undefined,
          is_enabled_globally: form.is_enabled_globally,
          ui_order: form.ui_order,
        });
        showSuccess(t("mod_catalog_created"));
      }
      setShowForm(false);
      await load();
    } catch (e: any) {
      showError(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (moduleKey: string) => {
    const confirmed = await showConfirm({
      title: t("bms_confirm_title"),
      text: t("mod_catalog_delete_confirm"),
      confirmText: t("delete"),
      cancelText: t("cancel"),
      icon: "warning",
    });
    if (!confirmed) return;
    try {
      await aiChatbotsService.deleteModuleCatalogItem(moduleKey);
      showSuccess(t("mod_catalog_deleted"));
      await load();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || "Error";
      console.error("Delete module failed:", e);
      showError(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  };

  return (
    <div className="chatbots-page bms-page">
      <div className="page-header-card mb-3">
        <div className="page-header-text">
          <h1 className="d-flex align-items-center gap-2">
            <TbPuzzle /> {t("mod_catalog_title")}
          </h1>
          <p>{t("mod_catalog_desc")}</p>
        </div>
        <button className="bms-btn-primary" onClick={openCreate}>
          <TbPlus /> {t("mod_catalog_add")}
        </button>
      </div>

      <div className="p-3 pt-0">
        <div className="bms-section-card">
          {isLoading ? (
            <div className="d-flex justify-content-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : catalog.length === 0 ? (
            <div className="bms-empty" style={{ padding: "2rem" }}>
              <div className="bms-empty-icon"><TbPuzzle /></div>
              <p>{t("bms_no_data")}</p>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="bms-table table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}><TbArrowsSort size={14} /></th>
                    <th>{t("mod_col_key")}</th>
                    <th>{t("mod_col_name")}</th>
                    <th>{t("mod_col_description")}</th>
                    <th style={{ width: 100 }}>{t("mod_col_global")}</th>
                    <th style={{ width: 100 }}>{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {catalog.map((item) => (
                    <tr key={item.module_key}>
                      <td className="text-muted text-center">{item.ui_order}</td>
                      <td>
                        <code style={{ fontSize: "0.85rem", background: "var(--glass-bg, #f0f0f0)", padding: "2px 8px", borderRadius: 6 }}>
                          {item.module_key}
                        </code>
                      </td>
                      <td className="fw-semibold">{item.name}</td>
                      <td className="text-muted" style={{ fontSize: "0.85rem" }}>{item.description || "—"}</td>
                      <td className="text-center">
                        <span className={`bms-status ${item.is_enabled_globally ? "active" : "inactive"}`}>
                          {item.is_enabled_globally ? t("active") : t("inactive")}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button type="button" className="bms-action-btn edit" onClick={() => openEdit(item)} title={t("edit")}><TbEdit /></button>
                          <button type="button" className="bms-action-btn danger" onClick={() => handleDelete(item.module_key)} title={t("delete")}><TbTrash /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create / Edit Modal */}
      <Modal show={showForm} onHide={() => setShowForm(false)} className="bms-modal" centered>
        <Modal.Header closeButton className="bms-modal-header">
          <Modal.Title className="d-flex align-items-center gap-2">
            <TbPuzzle /> {editingKey ? t("mod_catalog_edit") : t("mod_catalog_add")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="bms-form-label">{t("mod_col_key")} *</Form.Label>
            <Form.Control
              type="text" value={form.module_key}
              onChange={(e) => setForm((p) => ({ ...p, module_key: e.target.value }))}
              disabled={!!editingKey}
              placeholder="e.g. crm, bms, faq_plus"
            />
            {!editingKey && <Form.Text className="text-muted">{t("mod_key_hint")}</Form.Text>}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="bms-form-label">{t("mod_col_name")} *</Form.Label>
            <Form.Control
              type="text" value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Booking Management"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="bms-form-label">{t("mod_col_description")}</Form.Label>
            <Form.Control
              as="textarea" rows={2} value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
          </Form.Group>
          <div className="d-flex gap-4">
            <Form.Group>
              <Form.Label className="bms-form-label">{t("mod_col_order")}</Form.Label>
              <Form.Control
                type="number" min={0} value={form.ui_order}
                onChange={(e) => setForm((p) => ({ ...p, ui_order: Number(e.target.value) }))}
                style={{ width: 100 }}
              />
            </Form.Group>
            <Form.Group className="d-flex align-items-end pb-1">
              <Form.Check
                type="switch"
                id="catalog-global-switch"
                label={t("mod_col_global")}
                checked={form.is_enabled_globally}
                onChange={(e) => setForm((p) => ({ ...p, is_enabled_globally: e.target.checked }))}
              />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer className="bms-modal-footer">
          <button className="bms-btn-secondary" onClick={() => setShowForm(false)}><TbX /> {t("cancel")}</button>
          <button className="bms-btn-primary" onClick={handleSave} disabled={saving || !form.module_key.trim() || !form.name.trim()}>
            {saving ? <Spinner size="sm" animation="border" /> : <TbCheck />} {t("save")}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ModuleCatalogPage;
